import { useSubmit } from "@certn/api";
import { showToast, Text } from "@certn/cascadia-react";
import { Form, Formik } from "formik";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { array, object, string } from "yup";

import { useCreateGroupContext } from "./CreateGroupContext";
import { CreateGroupPageLayout } from "./CreateGroupPageLayout";
import { UserPicker } from "../../../../components/user-picker/UserPicker";
import { Schemas } from "../../../../types";

export function CreateGroupUsersStep() {
  const { t } = useTranslation();
  const { customerGroup } = useCreateGroupContext();
  const navigate = useNavigate();

  const schema = object({
    // Assuming the IDs are strings
    users: array()
      .of(string().required().label(t("Member ID")))
      .min(1, t("At least one member is required"))
      .label(t("Members")),
  });

  const { submit } = useSubmit<Schemas["CustomerGroupCreateResponse"]>(
    `/api/internal/client-portal/customer-groups/create/`,
    {
      onSuccess: () => {
        navigate("/groups");
        showToast(
          t("The group {{groupName}} has been created.", {
            groupName: customerGroup.name,
          })
        );
      },
    }
  );

  return (
    <Formik
      initialValues={customerGroup}
      validationSchema={schema}
      onSubmit={(values) => submit(values)}
    >
      {({ isSubmitting, values, setFieldValue, errors, touched }) => {
        return (
          <Form noValidate>
            <CreateGroupPageLayout
              title={<Trans>Add Members</Trans>}
              submitting={isSubmitting}
            >
              {errors.users && touched.users ? (
                <Text color="background-danger-strong">{errors.users}</Text>
              ) : null}
              <UserPicker
                label={t("Select members to add to group")}
                selectedKeys={values.users ?? []}
                onChange={(userKeys) =>
                  new Promise((resolve) => {
                    setFieldValue("users", userKeys);
                    resolve();
                  })
                }
                onRemove={(userId) =>
                  new Promise((resolve) => {
                    setFieldValue(
                      "users",
                      values.users?.filter((user) => user !== userId)
                    );
                    resolve();
                  })
                }
              />
            </CreateGroupPageLayout>
          </Form>
        );
      }}
    </Formik>
  );
}
